/**
 * All existing permission "categories", which are analogous to the module names that contain *assignable* Permission
 * subclass definitions in indy-api `app/services/access_control/permissions/`. By assignable, we mean they are
 * are configured to be shown and assignable in the Admin UI. Since they are Ruby module names, they are formatted
 * in Ruby class/module PascalCase.
 *
 * For example, the category for the permissions `"Customer::View"`, `"Customer::Manage"`, etc. is `"Customer"`. The
 * following names and comments should match the indy-api permission modules; indy-api is be the source of truth.
 *
 * The only reason the object keys are also PascalCase instead of CONSTANT_CASE is this was the only way I could make
 * the convenient Typescript enum types below work.
 * @enum {string}
 */
export const PERMISSION_CATEGORIES = Object.freeze({
  /** Domains: Screens, Seat Charts */
  Auditorium: 'Auditorium',
  /** Domains: Circuits, Bank Settings, Location Zones, Payment Processors, Tax Types, User Classes */
  Circuit: 'Circuit',
  /** Domains: Items (Categories / Classes / Modifiers / Packages / Preparation / Variations) */
  Concession: 'Concession',
  /** Domains: Users, User Memberships, Orders, Order Payments */
  Customer: 'Customer',
  /** Domains: Data Imports and Exports, TMS Exports */
  Data: 'Data',
  /** Domains: Booking Weeks, Movies, Showings, Title Classes, Planning Elements */
  FilmProgramming: 'FilmProgramming',
  /** Domains: Booking Weeks, Movies, Showings, Title Classes, Planning Elements */
  FilmSettlement: 'FilmSettlement',
  /** Domains: Chart of Accounts, Journal Entries, Disputes, Transfers */
  Financial: 'Financial',
  /** Domains: Registers, Register Grids and Buttons, Cash Trays, Kiosks, Displays, Printers, Remote Hardware */
  HardwareAndRegisterStation: 'HardwareAndRegisterStation',
  /** Domains: Inventory Items and Recipes, Inventory Strategies, Stock Management */
  Inventory: 'Inventory',
  /** Domains: Analytics, Customer Notifications, Email Broadcasts, Form Entries, Short URL Links, Visitor Reviews */
  Marketing: 'Marketing',
  /** Domains: Kitchen Displays, Deliveries, Delivery Locations, Delivery Types */
  Kitchen: 'Kitchen',
  /** Domains: Activities */
  NonFilmProgramming: 'NonFilmProgramming',
  /** Domains: Reports */
  OperationsReporting: 'OperationsReporting',
  /** Domains: Cash Counts, Delivery Locations, Delivery Types */
  PointOfSale: 'PointOfSale',
  /** Domains: Discounts, Gift Cards, Vouchers */
  Promotions: 'Promotions',
  /** Domains: Roles, Permissions */
  Security: 'Security',
  /** Domains: Pages, Sections, Content Blocks, Site Navigation, News Items, Signage */
  SiteContent: 'SiteContent',
  /** Domains: Site Configuration and Schedules, Membership Types, Print Templates, External Ticketing Clients */
  Site: 'Site',
  /** Domains: Box Office Reports, Studios */
  StudioReporting: 'StudioReporting',
  /** Domains: Employee Users, Job Types, Time Clock Sessions */
  Team: 'Team',
  /** Domains: Ticket Types, Price Cards, Screens, Seat Charts */
  Ticketing: 'Ticketing',
  /** Domains: Tip Pools, Tip Pool Types, Tip Shares, Tip Shares Rules, Tips */
  Tipping: 'Tipping'
})

/**
 * Type of all possible permission categories. Can be used to annotate functions or compose types.
 * @typedef {typeof PERMISSION_CATEGORIES[keyof typeof PERMISSION_CATEGORIES]} PermissionCategory
 */

/**
 * All possible permission actions, which are analogous to the class names of *assignable* Permission subclasses.
 * @enum {string}
 */
export const DEFAULT_PERMISSION_ACTIONS = Object.freeze({
  /** Allows visiting the model's pages and loading/viewing data. */
  View: 'View',
  /** Allows creating and editing of model data, as well as triggering API actions. */
  Manage: 'Manage',
  /** Allows archiving or deleting model data. */
  Delete: 'Delete'
})

/**
 * Type of default permission actions. Can be used to annotate functions or compose types.
 * @typedef {typeof DEFAULT_PERMISSION_ACTIONS[keyof typeof DEFAULT_PERMISSION_ACTIONS]} DefaultPermissionAction
 */

/**
 * Creates a readonly object for a category where the keys are the default permission actions and
 * the values are strings built by concatenating the category and action with a double colon.
 * @param {PermissionCategory} category
 */
const generateDefaultPermissionsForCategory = (category) => {
  return Object.freeze(_.mapValues(DEFAULT_PERMISSION_ACTIONS, (action) => `${category}::${action}`))
}

/**
 * A two-layer map of all possible permissions, including all categories and their default permissions.
 * For example: `PERMISSIONS_BY_CATEGORY.CUSTOMER.VIEW` is the string `"Customer::View"`.
*/
export const PERMISSIONS_BY_CATEGORY = Object.freeze({
  ..._.mapValues(PERMISSION_CATEGORIES, generateDefaultPermissionsForCategory),
  [PERMISSION_CATEGORIES.Customer]: Object.freeze({
    ...generateDefaultPermissionsForCategory(PERMISSION_CATEGORIES.Customer),
    /** View user Personal Identifiable Information (PII) fields. */
    ViewSensitiveData: 'Customer::ViewSensitiveData',
    /** Access user memberships. */
    ViewMemberships: 'Customer::ViewMemberships',
    /** Create and edit user memberships. */
    ManageMemberships: 'Customer::ManageMemberships',
    /** Cancel user memberships. */
    CancelMemberships: 'Customer::CancelMemberships',
    /** Create and edit non-membership benefits. For example, rewards points. */
    ManageBenefits: 'Customer::ManageBenefits',
    /** Create and edit payment methods and order payments. */
    ViewPayments: 'Customer::ViewPayments',
    /** Create and edit non-membership benefits. For example, rewards points. */
    ManagePayments: 'Customer::ManagePayments'
  }),
  [PERMISSION_CATEGORIES.HardwareAndRegisterStation]: Object.freeze({
    ...generateDefaultPermissionsForCategory(PERMISSION_CATEGORIES.HardwareAndRegisterStation),
    /** Configure which printers Locations route to. Does not allow other printer configuration like Manage does. */
    ManagePrinterRouting: 'HardwareAndRegisterStation::ManagePrinterRouting'
  }),
  [PERMISSION_CATEGORIES.Security]: Object.freeze({
    /** Allows visiting the model's pages and loading/viewing data. */
    View: 'Security::View',
    /** Allows archiving or deleting model data. */
    Delete: 'Security::Delete',
    /** Add or remove Roles, configure Role permissions */
    ManageCircuitRoles: 'Security::ManageCircuitRoles',
    /** Add or remove Roles to Users, or Users to Roles */
    ManageUserRoles: 'Security::ManageUserRoles',
    /** Promote or demote Admins and Employees. Also change their authorized Sites/Circuits. Includes PromoteUserToEmployee rights. */
    PromoteUserToAdminOrEmployee: 'Security::PromoteUserToAdminOrEmployee',
    /** Promote or demote Employees only. Also change employee authorized Sites/Circuits. */
    PromoteUserToEmployee: 'Security::PromoteUserToEmployee'
  }),
  [PERMISSION_CATEGORIES.Team]: Object.freeze({
    ...generateDefaultPermissionsForCategory(PERMISSION_CATEGORIES.Team),
    /** Approve time clock sessions. */
    ApproveTimeClockSessions: 'Team::ApproveTimeClockSessions',
    /** Bulk approve time clock sessions. */
    BulkApproveTimeClockSessions: 'Team::BulkApproveTimeClockSessions'
  })
})

/**
 * Type of all possible permissions by category, including custom permissions. Can be used to annotate functions or compose types.
 * @typedef {typeof PERMISSIONS_BY_CATEGORY} PermissionsByCategory
 */
